import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API_BASE } from "../services/api";
import { Helmet } from "react-helmet-async";
import SmartImg from "../components/SmartImg";
import LazyMount from "../components/LazyMount";
import LiteYouTube from "../components/LiteYouTube";
import LiteSpotifyTrack from "../components/LiteSpotifyTrack";
import LiteInstagram from "../components/LiteInstagram";

const imgSrc = (url) => (url?.startsWith("http") ? url : `${API_BASE}${url || ""}`);

// Enquiry helpers
const ENQUIRY_LINE =
  "For any third party work or brand collaborations, please contact HLT Management at: hlt-mgmt@hey-lets-travel.com";
const containsEnquiry = (txt) =>
  String(txt || "").toLowerCase().includes("hlt-mgmt@hey-lets-travel.com");
const HLT_WHATSAPP_SHORT_LINK = "https://wa.me/message/5UT3QJZMUBKNP1";

// --- Embeds ---
const getYouTubeId = (url) => {
  try {
    const u = new URL(url);
    const vParam = u.searchParams.get("v");
    if (vParam) return vParam;
    const parts = u.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (/^shorts$/i.test(parts[0]) || /^embed$/i.test(parts[0])) return last;
    return last;
  } catch {
    return null;
  }
};

const embedSoundCloud = (url) =>
  url?.includes("soundcloud.com")
    ? `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}`
    : null;

// Safe Spotify track-id extractor
const getSpotifyTrackId = (url = "") => {
  const m = url?.match(/spotify\.com\/track\/([A-Za-z0-9]+)/);
  return m ? m[1] : null;
};

export default function RosterDetail() {
  const { category, slug } = useParams();

  const [it, setIt] = useState(null);
  const [err, setErr] = useState("");
  const [showTop, setShowTop] = useState(false);

  // 🔑 Hook must be ABOVE any early returns
  const [playSpotify, setPlaySpotify] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/public/roster/${category.toUpperCase()}/${slug}`);
        setIt(data);
      } catch {
        setErr("This profile is not available.");
      }
    })();
  }, [category, slug]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (err)
    return (
      <div className="page">
        <section className="card text-red-200">{err}</section>
      </div>
    );
  if (!it)
    return (
      <div className="page">
        <section className="card">Loading…</section>
      </div>
    );

  const isDJ = category.toUpperCase() === "DJ";
  const ytIds = (it.youtubeUrls || []).map(getYouTubeId).filter(Boolean);
  const ig = it.instagramUrls || [];
  const scTrack = isDJ ? embedSoundCloud(it.popularSongUrl) : null;
  const hasMedia = ytIds.length > 0 || ig.length > 0;

  // Spotify bits
  const spotifyTrackId = isDJ ? getSpotifyTrackId(it.popularSongUrl) : null;

  const enquiryOn = it.inquiry ?? it.showEnquiry ?? false;

  // SEO
  const canonicalUrl = `https://www.hey-lets-travel.com/talent/${category.toLowerCase()}/${it.slug}`;
  const ogImage = it.imageUrl
    ? (it.imageUrl.startsWith("http") ? it.imageUrl : `${API_BASE}${it.imageUrl}`)
    : undefined;
  const metaTitle = it.metaTitle || `${it.name} | ${isDJ ? "DJ" : "Creator"} | HLT`;
  const metaDesc = (it.metaDescription || it.blurb || "").slice(0, 160);

  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": it.name,
    "description": metaDesc || undefined,
    "image": ogImage ? [ogImage] : undefined,
    "homeLocation": it.location || undefined,
    "sameAs": [
      it.instagramHandle &&
      (it.instagramHandle.startsWith("http")
        ? it.instagramHandle
        : `https://instagram.com/${String(it.instagramHandle).replace(/^@/, "")}`),
      it.spotifyUrl,
      it.soundcloudUrl,
      it.tiktokHandle &&
      (it.tiktokHandle.startsWith("http")
        ? it.tiktokHandle
        : `https://www.tiktok.com/@${String(it.tiktokHandle).replace(/^@/, "")}`)
    ].filter(Boolean)
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        {metaDesc && <meta name="description" content={metaDesc} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={metaTitle} />
        {metaDesc && <meta property="og:description" content={metaDesc} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        {metaDesc && <meta name="twitter:description" content={metaDesc} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        <script type="application/ld+json">{JSON.stringify(personJsonLd)}</script>
      </Helmet>

      <SEO
        meta={{
          title: metaTitle,
          description: metaDesc,
          keywords: it.metaKeywords,
          indexable: it.indexable !== false,
        }}
      />

      <div className="page space-y-6">
        <div>
          <Link to="/roster" className="btn-secondary">← Back to Talent</Link>
        </div>

        {/* HERO */}
        <section className="card p-4 md:p-5">
          <div className="grid gap-5 md:grid-cols-[minmax(260px,420px)_1fr] items-start">
            <div className="mx-auto w-full max-w-[480px]">
              {it.imageUrl ? (
                <SmartImg
                  src={imgSrc(it.imageUrl)}
                  alt={it.name}
                  className="w-full aspect-[4/5] object-cover rounded-2xl border border-white/10 shadow-lg"
                  width={480}
                  height={600}
                />
              ) : (
                <div className="w-full aspect-[4/5] rounded-2xl border border-white/10 bg-white/5 grid place-items-center text-slate-400">
                  No image
                </div>
              )}
            </div>

            <div className="space-y-3 md:space-y-4">
              <h1 className="font-display text-3xl md:text-4xl text-white">{it.name}</h1>

              <div className="text-slate-300 space-y-1">
                {it.location && (
                  <p><span className="text-slate-400">Location:</span> {it.location}</p>
                )}
                {isDJ && (it.genre || it.inspiration) && (
                  <>
                    {it.genre && <p><span className="text-slate-400">Genre:</span> {it.genre}</p>}
                    {it.inspiration && <p><span className="text-slate-400">Inspiration:</span> {it.inspiration}</p>}
                  </>
                )}
              </div>

              {it.blurb && (
                <div className="prose prose-invert max-w-none text-slate-200">
                  <p className="whitespace-pre-line">{it.blurb}</p>
                </div>
              )}

              {/* Enquiry */}
              {(() => {
                const blurbHasEmail = containsEnquiry(it.blurb);
                const showEmailBlock = enquiryOn ? !blurbHasEmail : !blurbHasEmail && (it.showEnquiry ?? false);
                if (!showEmailBlock && !enquiryOn) return null;
                return (
                  <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-slate-200 space-y-1">
                    {showEmailBlock && <p>{ENQUIRY_LINE}</p>}
                    {enquiryOn && (
                      <p className="text-sm">
                        Or chat with us on{" "}
                        <a
                          href={HLT_WHATSAPP_SHORT_LINK}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 underline decoration-emerald-400/60 hover:text-white"
                          title="WhatsApp HLT Management"
                        >
                          <WhatsAppIcon className="h-4 w-4" />
                          WhatsApp
                        </a>
                        .
                      </p>
                    )}
                  </div>
                );
              })()}

              <SocialIcons it={it} enquiryOn={enquiryOn} />
            </div>
          </div>
        </section>

        {/* MEDIA */}
        {hasMedia ? (
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white px-1">Featured Media</h2>

            <div className="grid gap-4 md:grid-cols-2">
              {/* YouTube (lite + lazymount) */}
              {ytIds.map((id, i) => (
                <div key={`yt-${id}-${i}`} className="card overflow-hidden">
                  <div className="relative w-full aspect-video rounded-xl overflow-hidden">
                    <LazyMount>
                      <LiteYouTube id={id} title={`YouTube ${i + 1}`} />
                    </LazyMount>
                  </div>
                </div>
              ))}

              {/* Instagram (first one auto-loads on view) */}
              {ig.map((url, i) => (
                <LiteInstagram
                  key={`ig-${i}`}
                  url={url}
                  autoLoadOnView={i === 0}
                />
              ))}
            </div>
          </section>
        ) : (
          it.popularAlbumImageUrl && (
            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-white px-1">Featured</h2>
              <div className="flex justify-center">
                <SmartImg
                  src={imgSrc(it.popularAlbumImageUrl)}
                  alt="Popular release"
                  className="w-full max-w-sm rounded-2xl border border-white/10 shadow-lg"
                  width={640}
                  height={640}
                />
              </div>
            </section>
          )
        )}

        {/* POPULAR SONG */}
        {isDJ && (spotifyTrackId || scTrack) && (
          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-white px-1">Most Popular Song</h2>
            <div className="card overflow-hidden">
              {spotifyTrackId ? (
                <LazyMount>
                  <LiteSpotifyTrack
                    id={spotifyTrackId}
                    title={`${it.name} — Popular track`}
                    coverUrl={imgSrc(it.popularAlbumImageUrl)}
                    playing={playSpotify}
                    onPlayRequest={() => setPlaySpotify(true)}
                  />
                </LazyMount>
              ) : (
                <iframe
                  title="SoundCloud Player"
                  src={scTrack}
                  width="100%"
                  height="166"
                  scrolling="no"
                  frameBorder="no"
                  loading="lazy"
                />
              )}
            </div>
          </section>
        )}
      </div>

      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-40 btn-primary shadow-lg"
          aria-label="Back to top"
        >
          ↑ Top
        </button>
      )}

      <style>{`
        .ig-embed { display: grid; place-items: center; padding: .5rem; }
        .ig-embed .instagram-media { margin: 0 !important; max-width: 100% !important; }
      `}</style>
    </>
  );
}

function SocialIcons({ it, enquiryOn }) {
  const norm = (h) => h?.replace(/^@/, "");
  const HLT_WHATSAPP_SHORT_LINK = "https://wa.me/message/5UT3QJZMUBKNP1";

  const links = [
    it.instagramHandle && {
      key: "instagram",
      label: "Instagram",
      href: it.instagramHandle.startsWith("http")
        ? it.instagramHandle
        : `https://instagram.com/${norm(it.instagramHandle)}`,
    },
    it.spotifyUrl && { key: "spotify", label: "Spotify", href: it.spotifyUrl },
    it.soundcloudUrl && { key: "soundcloud", label: "SoundCloud", href: it.soundcloudUrl },
    it.tiktokHandle && {
      key: "tiktok",
      label: "TikTok",
      href: it.tiktokHandle.startsWith("http")
        ? it.tiktokHandle
        : `https://www.tiktok.com/@${norm(it.tiktokHandle)}`,
    },
    enquiryOn && {
      key: "whatsapp",
      label: "WhatsApp (HLT Management)",
      href: HLT_WHATSAPP_SHORT_LINK,
    },
  ].filter(Boolean);

  if (links.length === 0) return null;

  return (
    <div className="flex items-center gap-2 pt-1">
      {links.map((l) => (
        <a
          key={l.key}
          href={l.href}
          className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 p-2 hover:bg-white/10 transition"
          target="_blank"
          rel="noreferrer"
          aria-label={l.label}
          title={l.label}
        >
          {iconFor(l.key)}
        </a>
      ))}
    </div>
  );
}

function iconFor(name) {
  const common = "h-5 w-5";
  switch (name) {
    case "instagram":
      return (
        <svg className={common} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6zM18 6.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
        </svg>
      );
    case "spotify":
      return (
        <svg className={common} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 1.8a10.2 10.2 0 1 0 0 20.4A10.2 10.2 0 0 0 12 1.8zm4.53 14.9a.9.9 0 0 1-1.25.31c-3.42-2.09-7.73-2.56-12.29-1.36a.9.9 0 1 1-.46-1.73c5.02-1.33 9.77-.8 13.56 1.49.43.26.57.82.24 1.29zm.86-3.09a1 1 0 0 1-1.38.36c-3.95-2.36-9.96-3.04-14.35-1.63a1 1 0 1 1-.59-1.91c4.86-1.5 11.39-.75 15.83 1.9a1 1 0 0 1 .49 1.28zm.13-3.2a1.1 1.1 0 0 1-1.52.39C11.5 7.7 6.3 7.2 2.57 8.4a1.1 1.1 0 1 1-.66-2.11C6.15 5.9 12 6.5 16.5 9.2a1.1 1.1 0 0 1 .52 1.2z" />
        </svg>
      );
    case "soundcloud":
      return (
        <svg className={common} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.5 10.5a3.5 3.5 0 1 1 0 7H6.75a3.25 3.25 0 1 1 .77-6.4 4.75 4.75 0 0 1 8.98-1.77c.65-.29 1.35-.44 2.0-.44z" />
        </svg>
      );
    case "tiktok":
      return (
        <svg className={common} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M13 3h2.1c.35 1.9 1.68 3.23 3.58 3.58V9c-1.68-.03-3.16-.6-4.54-1.73v6.52a5.79 5.79 0 1 1-2.14-4.46V3zM9.9 12.4a2.59 2.59 0 1 0 2.59 2.59v-3.5a2.58 2.58 0 0 0-2.59.91z" />
        </svg>
      );
    case "whatsapp":
      return <WhatsAppIcon className={common} />;
    default:
      return null;
  }
}

function WhatsAppIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.52 3.48A11.93 11.93 0 0 0 12.01 0C5.39 0 .03 5.36.03 11.98c0 2.11.55 4.17 1.6 5.99L0 24l6.22-1.62a11.97 11.97 0 0 0 5.78 1.49h.01c6.62 0 11.99-5.36 11.99-11.98a11.9 11.9 0 0 0-3.48-8.41zM12.01 21.8h-.01a9.82 9.82 0 0 1-4.99-1.36l-.36-.22-3.69.96.99-3.6-.24-.37a9.83 9.83 0 0 1-1.51-5.21c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.11 1.02 6.97 2.88a9.78 9.78 0 0 1 2.88 6.97c0 5.42-4.42 9.8-9.89 9.8zm5.6-7.35c-.31-.16-1.82-.9-2.1-1a.63.63 0 0 0-.61.08c-.17.12-.71.65-.87.87a.56.56 0 0 1-.6.2 9.82 9.82 0 0 1-3.06-1.89 11.53 11.53 0 0 1-2.13-2.64.63.63 0 0 1 .18-.85c.09-.08.2-.21.3-.31.1-.1.18-.2.27-.32a.6.6 0 0 0 .05-.64c-.06-.16-.48-1.17-.66-1.6-.18-.44-.37-.38-.54-.39h-.46a.9.9 0 0 0-.65.31 2.73 2.73 0 0 0-.84 2.03c0 1.2.87 2.36 1 2.53.12.16 1.73 2.66 4.2 3.74.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.46-.59 1.67-1.16.21-.57.21-1.06.14-1.16a.7.7 0 0 0-.33-.32z" />
    </svg>
  );
}

function SEO({ meta }) {
  document.title = meta.title || document.title;
  const setMeta = (name, content) => {
    if (!content) return;
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  setMeta("description", meta.description || "");
  setMeta("keywords", meta.keywords || "");
  // Only noindex when explicitly false
  const robots = meta.indexable === false ? "noindex,nofollow" : "index,follow";
  let r = document.querySelector('meta[name="robots"]');
  if (!r) {
    r = document.createElement("meta");
    r.setAttribute("name", "robots");
    document.head.appendChild(r);
  }
  r.setAttribute("content", robots);
  return null;
}
